import { type PrimitiveAtom, useAtomValue } from "jotai";
import { isNil, uniqBy } from "lodash";
import { useMemo } from "react";
import { type MeReadDto } from "@/config/apiClient";
import {
  OrganisationTypeEnum,
  ServiceTypeEnum,
} from "@/config/apiClient.types";
import { userAtom } from "@/store";

type UserAtomType =
  typeof userAtom extends PrimitiveAtom<infer T> ? T : undefined;

export type StructuredServicesByOrganisations = Array<{
  organisation: NonNullable<UserAtomType>["Services"][number]["Organisation"];
  services: Array<
    MeReadDto["Services"][number] & {
      ChildServices: Array<
        StructuredServicesByOrganisations[number]["services"][number]
      >;
    }
  >;
}>;

export const useGetStructuredServicesByOrganisation = (): {
  structuredServicesByOrganisations: StructuredServicesByOrganisations;
  serviceToSelectByDefault: MeReadDto["Services"][number] | undefined;
} => {
  const userAtomValue = useAtomValue(userAtom);

  const res = useMemo<{
    structuredServicesByOrganisations: StructuredServicesByOrganisations;
    serviceToSelectByDefault: MeReadDto["Services"][number] | undefined;
  }>(() => {
    if (isNil(userAtomValue)) {
      return {
        structuredServicesByOrganisations: [],
        serviceToSelectByDefault: undefined,
      };
    }

    const userOrganisations = uniqBy(
      userAtomValue.Services.map(
        (userService) => userService.Organisation
      ).filter(
        (organisation) => organisation.type === OrganisationTypeEnum.CLIENT
      ),
      "id"
    );

    const structuredServicesByOrganisationsResult =
      userOrganisations.reduce<StructuredServicesByOrganisations>(
        (acc, currentUserOrganisation) => {
          const processed: Array<MeReadDto["Services"][number]> = [];
          const organisationServices: StructuredServicesByOrganisations[number]["services"] =
            [];

          const getServiceWithChildren = (
            service: MeReadDto["Services"][number]
          ): StructuredServicesByOrganisations[number]["services"][number] => {
            const children = userAtomValue.Services.filter(
              (userService) =>
                userService.type === ServiceTypeEnum.CLIENT &&
                userService.organisationId === currentUserOrganisation.id &&
                userService.parentServiceId === service.id &&
                !processed.find((p) => p.id === userService.id)
            );

            processed.push(service, ...children);

            return {
              ...service,
              ChildServices: children.map((child) =>
                getServiceWithChildren(child)
              ),
            };
          };

          // Itération sur les services racines
          userAtomValue.Services.filter(
            (userService) =>
              userService.type === ServiceTypeEnum.CLIENT &&
              userService.organisationId === currentUserOrganisation.id
          ).forEach((service) => {
            if (!processed.find((p) => p.id === service.id)) {
              organisationServices.push(getServiceWithChildren(service));
            }
          });

          acc.push({
            organisation: currentUserOrganisation,
            services: organisationServices,
          });

          return acc;
        },
        []
      );

    const findFirstLeaf = (
      services: StructuredServicesByOrganisations[number]["services"]
    ): MeReadDto["Services"][number] | undefined =>
      services.reduce<MeReadDto["Services"][number] | undefined>(
        (found, service) =>
          found ??
          (service.ChildServices.length === 0
            ? service
            : findFirstLeaf(service.ChildServices)),
        undefined
      );

    const serviceToSelectByDefault =
      structuredServicesByOrganisationsResult.reduce<
        MeReadDto["Services"][number] | undefined
      >((found, org) => found ?? findFirstLeaf(org.services), undefined);

    return {
      structuredServicesByOrganisations:
        structuredServicesByOrganisationsResult,
      serviceToSelectByDefault,
    };
  }, [userAtomValue]);

  return res;
};
